/**
 * useCardSettings / useCardSettingsMutations
 *
 * Same pattern as useBranding / useBrandingMutations — keeps the two
 * settings-type hooks shaped identically so components can migrate
 * between them without cognitive load.
 *
 * Consumers:
 *   IdCardsPage.jsx — the main form
 *   (potentially) StaffCardExport.jsx — if it ever needs to check
 *     the current template before generating (currently it just
 *     passes the whole tenant object through to generateCardPdf)
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * Fetches current card settings for the signed-in admin's tenant.
 * Returns { card, loading, error, refresh }.
 */
export function useCardSettings() {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/dashboard/card-settings');
      setCard(res.card);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { card, loading, error, refresh };
}

/**
 * Mutation methods. Doesn't manage its own state — consumer calls
 * refresh() from useCardSettings after a successful mutation.
 */
export function useCardSettingsMutations() {
  const updateCardSettings = useCallback(async (partial) => {
    const res = await api.put('/api/dashboard/card-settings', partial);
    return res.card;
  }, []);

  const uploadBottomImage = useCallback(async (file) => {
    const form = new FormData();
    form.append('bottom_image', file);
    const res = await api.post('/api/dashboard/card-settings/bottom-image', form);
    return res;
  }, []);

  const deleteBottomImage = useCallback(async () => {
    const res = await api.delete('/api/dashboard/card-settings/bottom-image');
    return res;
  }, []);

  return { updateCardSettings, uploadBottomImage, deleteBottomImage };
}
